import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BuildingIcon, ChevronsUpDownIcon, ClipboardListIcon, KanbanSquareIcon, LayoutDashboardIcon, LayoutTemplateIcon, LogOutIcon, MenuIcon, PlugIcon, SettingsIcon, Trash2Icon, UsersIcon, UsersRoundIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBrandTheme } from '../hooks/useBrandTheme';
import { Permission } from '../config/permissions';
import { ROLE_LABELS } from '../types';
import { BrandLogo } from '../components/shared/BrandLogo';
import { Avatar } from '../components/ui/Avatar';
import { Dropdown } from '../components/ui/Dropdown';
import { Drawer } from '../components/ui/Drawer';
interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  permission?: Permission;
}
const NAV: NavItem[] = [{
  to: '/app/dashboard',
  label: 'Dashboard',
  icon: LayoutDashboardIcon
}, {
  to: '/app/leads',
  label: 'Leads',
  icon: UsersIcon
}, {
  to: '/app/pipeline',
  label: 'Embudo',
  icon: KanbanSquareIcon
}, {
  to: '/app/formularios',
  label: 'Formularios',
  icon: ClipboardListIcon,
  permission: 'forms.manage'
}, {
  to: '/app/landings',
  label: 'Landings',
  icon: LayoutTemplateIcon,
  permission: 'landings.manage'
}, {
  to: '/app/papelera',
  label: 'Papelera',
  icon: Trash2Icon
}, {
  to: '/app/equipo',
  label: 'Equipo',
  icon: UsersRoundIcon,
  permission: 'team.manage'
}, {
  to: '/app/integraciones',
  label: 'Integraciones',
  icon: PlugIcon,
  permission: 'integrations.manage'
}, {
  to: '/app/configuracion',
  label: 'Configuración',
  icon: SettingsIcon,
  permission: 'settings.manage'
}];
function navClass({
  isActive
}: {
  isActive: boolean;
}): string {
  return ['flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors min-h-[44px]', isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-surface hover:text-foreground'].join(' ');
}
export function AppLayout() {
  const {
    organization,
    user,
    role,
    hasPermission,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // La identidad visual de la empresa se aplica a toda la plataforma.
  useBrandTheme(organization);
  const items = NAV.filter(item => !item.permission || hasPermission(item.permission));
  async function handleLogout() {
    await logout();
    navigate('/login');
  }
  const nav = <nav className="flex flex-col gap-1" aria-label="Navegación principal" data-id="element-602" data-mp="30gugoc93fr95">
      {items.map(item => <NavLink key={item.to} to={item.to} className={navClass} onClick={() => setMenuOpen(false)} data-id="element-603" data-mp="2vhzz1dmv48qm">
          <item.icon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-604" data-mp="2qi58xcawvn6m" />
          {item.label}
        </NavLink>)}
    </nav>;
  const workspaceCard = <div className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2.5" data-id="element-605" data-mp="11zkafgfqhude">
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-primary/10" data-id="element-606" data-mp="16031375w4ue0">
        {organization?.brand.logoUrl ? <img src={organization.brand.logoUrl} alt="" className="h-6 w-6 object-contain" data-id="element-607" data-mp="1b0ie1xm9fzvg" /> : <BuildingIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-608" data-mp="ibjt6sqr6jmg" />}
      </span>
      <span className="min-w-0 flex-1" data-id="element-609" data-mp="n9lelkbniv13">
        <span className="block truncate text-sm font-semibold text-foreground" data-id="element-610" data-mp="nao6nvkpn3il">
          {organization?.name ?? 'Mi empresa'}
        </span>
        <span className="block truncate text-xs text-muted-foreground" data-id="element-611" data-mp="wzo4hbnh7sv7">
          {organization?.sector}
        </span>
      </span>
      <ChevronsUpDownIcon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" data-id="element-612" data-mp="3vowr2gd2wnzo" />
    </div>;
  return <div className="flex min-h-screen w-full bg-surface" data-id="element-613" data-mp="4hafm1k4bh89">
      {/* Sidebar escritorio */}
      <aside className="sticky top-0 hidden h-screen w-64 flex-none flex-col border-r border-border bg-background p-4 lg:flex" data-id="element-614" data-mp="266q3woivz6if">
        <div className="mb-6 px-1" data-id="element-615" data-mp="2fycb89s3slfw">
          <BrandLogo src={organization?.brand.logoUrl ?? organization?.logoUrl} size={26} alt={organization?.name ?? 'Sistema AVA'} data-id="element-616" data-mp="2fzdyrdqb30ei" />
        </div>
        <div className="mb-4" data-id="element-617" data-mp="2pvqsgblupe99">{workspaceCard}</div>
        {nav}
        <div className="mt-auto pt-4" data-id="element-618" data-mp="2kxyylc1nbi2f">
          <button type="button" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface hover:text-foreground" data-id="element-619" data-mp="2kwx9w4rpix28">
            <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-620" data-mp="14ixcp1k8xlio" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Menú móvil */}
      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} side="left" title="Menú" data-id="element-621" data-mp="zmgsgig63rav">
        <div className="space-y-4" data-id="element-622" data-mp="3g6j1c1p6a8vl">
          {workspaceCard}
          {nav}
          <button type="button" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-surface" data-id="element-623" data-mp="3g8n2wczlmvsp">
            <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-624" data-mp="2sgopnnglaqcx" />
            Cerrar sesión
          </button>
        </div>
      </Drawer>

      <div className="flex min-w-0 flex-1 flex-col" data-id="element-625" data-mp="2xhvw9j6zxji0">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur sm:px-6" data-id="element-626" data-mp="2jh1n2yc4jeia">
          <button type="button" onClick={() => setMenuOpen(true)} aria-label="Abrir menú" className="flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-surface lg:hidden" data-id="element-627" data-mp="2nin5t0bcan0e">
            <MenuIcon className="h-5 w-5" data-id="element-628" data-mp="1unytu8skvfpw" />
          </button>
          <div className="min-w-0 flex-1" data-id="element-629" data-mp="1ulcdbjnplpvj">
            <p className="truncate text-sm font-semibold text-foreground" data-id="element-630" data-mp="1pnkkmnf8pzqa">
              {organization?.name}
            </p>
            <p className="truncate text-xs text-muted-foreground" data-id="element-631" data-mp="1lnk4mychtgh4">
              {role ? ROLE_LABELS[role] : ''}
            </p>
          </div>
          <Dropdown align="right" trigger={<span className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-surface" data-id="element-633" data-mp="2dgat2mnxq0vt">
                <Avatar name={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`} size="sm" data-id="element-634" data-mp="29epmtk5wokul" />
                <span className="hidden text-sm font-medium text-foreground sm:inline" data-id="element-635" data-mp="1znbd6jk8q4nq">
                  {user?.firstName}
                </span>
              </span>} items={[{
          label: 'Configuración de empresa',
          icon: <SettingsIcon className="h-4 w-4" data-id="element-636" data-mp="2zmlpkyn87khe" />,
          onSelect: () => navigate('/app/configuracion')
        }, {
          label: 'Cerrar sesión',
          icon: <LogOutIcon className="h-4 w-4" data-id="element-637" data-mp="39kt1h32nrccj" />,
          destructive: true,
          onSelect: handleLogout
        }]} data-id="element-632" data-mp="2ddyg6nx1agpm" />
        </header>

        <main className="min-w-0 flex-1 px-4 py-8 sm:px-6 lg:px-10" data-id="element-638" data-mp="3dka9a35k4lkv">
          <Outlet data-id="element-639" data-mp="3ikoivpmt43l0" />
        </main>
      </div>
    </div>;
}